import axios from "axios";
import { get, ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { encryptPassword } from "../utils/string";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import types from "../types";
import { LaunchError } from "../utils/errors";

const DEFAULT_BYOK_CONFIGURATION_PUBLIC = {
  defaultPaymentMode: "credits",
  providers: {},
};

const normalizeByokPublicConfiguration = (data = {}) => ({
  defaultPaymentMode:
    data?.defaultPaymentMode === "byok"
      ? "byok"
      : DEFAULT_BYOK_CONFIGURATION_PUBLIC.defaultPaymentMode,
  providers: data?.providers || {},
});

const setByokConfigurationPublic = (payload) => ({
  type: types.SET_BYOK_CONFIGURATION_PUBLIC,
  payload,
});

export const setByokConfigurationLoading = (payload) => ({
  type: types.SET_BYOK_CONFIGURATION_LOADING,
  payload,
});

export const startListenerByokConfiguration =
  (onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { agencyID } = dispatch(getPathDatabaseByRole());
      if (!agencyID) {
        dispatch(setByokConfigurationPublic(DEFAULT_BYOK_CONFIGURATION_PUBLIC));
        onReferenceAvailable(null);
        return true;
      }

      dispatch(setByokConfigurationLoading(true));
      const dbRef = ref(db, `byokConfigurations/${agencyID}/public`);

      onValue(dbRef, (snapshot) => {
        const nextConfiguration = snapshot.exists()
          ? normalizeByokPublicConfiguration(snapshot.val())
          : DEFAULT_BYOK_CONFIGURATION_PUBLIC;
        dispatch(setByokConfigurationPublic(nextConfiguration));
      });

      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerByokConfiguration",
        })
      );
      dispatch(setByokConfigurationPublic(DEFAULT_BYOK_CONFIGURATION_PUBLIC));
      dispatch(setByokConfigurationLoading(false));
      return false;
    }
  };

export const startGetByokConfiguration =
  ({ force = false } = {}) =>
  async (dispatch, getState) => {
    try {
      const paymentsState = getState()?.payments || {};
      const currentConfiguration = paymentsState?.byokConfigurationPublic;
      if (!force && paymentsState?.hasLoadedByokConfiguration) {
        return currentConfiguration;
      }

      dispatch(setByokConfigurationLoading(true));
      const { agencyID } = dispatch(getPathDatabaseByRole());
      if (!agencyID) {
        dispatch(setByokConfigurationPublic(DEFAULT_BYOK_CONFIGURATION_PUBLIC));
        return DEFAULT_BYOK_CONFIGURATION_PUBLIC;
      }

      const snapshot = await get(
        ref(db, `byokConfigurations/${agencyID}/public`)
      );
      const nextConfiguration = snapshot.exists()
        ? normalizeByokPublicConfiguration(snapshot.val())
        : DEFAULT_BYOK_CONFIGURATION_PUBLIC;

      dispatch(setByokConfigurationPublic(nextConfiguration));
      return nextConfiguration;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetByokConfiguration",
        })
      );
      dispatch(setByokConfigurationLoading(false));
      return DEFAULT_BYOK_CONFIGURATION_PUBLIC;
    }
  };

export const startSaveByokConfiguration =
  ({ providers = {}, defaultPaymentMode, removeProviders = [] } = {}) =>
  async (dispatch) => {
    try {
      dispatch(setByokConfigurationLoading(true));
      const token = await dispatch(renewToken());

      const encryptedProviders = Object.fromEntries(
        Object.entries(providers || {})
          .map(([provider, apiKey]) => [provider, String(apiKey || "").trim()])
          .filter((entry) => Boolean(entry[1]))
          .map(([provider, apiKey]) => [provider, encryptPassword(apiKey)])
      );

      const normalizedRemoveProviders = (removeProviders || [])
        .map((provider) => String(provider || "").trim())
        .filter(Boolean);

      const body = {
        providers: encryptedProviders,
        defaultPaymentMode,
        ...(normalizedRemoveProviders.length
          ? { removeProviders: normalizedRemoveProviders }
          : {}),
      };

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("saveByokConfiguration")}/saveByokConfiguration`,
        data: body,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data?.ok) {
        dispatch(setByokConfigurationLoading(false));
        return data || { ok: false };
      }

      const nextConfiguration = normalizeByokPublicConfiguration(data.data);
      dispatch(setByokConfigurationPublic(nextConfiguration));
      return {
        ok: true,
        data: nextConfiguration,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveByokConfiguration",
        })
      );
      dispatch(setByokConfigurationLoading(false));
      return { ok: false };
    }
  };
